import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import {
    Search as SearchIcon,
    User,
    FileText,
    ClipboardList,
    UserCheck,
    Truck,
    Calendar,
    DollarSign,
    Building2,
    ArrowUpFromLine,
    Warehouse,
    CheckCircle2,
    XCircle,
    Clock,
    Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_META = {
    client:     { label: 'Client',     icon: UserCheck,      color: 'text-emerald-600', bg: 'bg-emerald-50',   border: 'border-emerald-200',   dot: 'bg-emerald-500' },
    devis:      { label: 'Devis',      icon: FileText,       color: 'text-blue-600',    bg: 'bg-blue-50',      border: 'border-blue-200',      dot: 'bg-blue-500' },
    inventaire: { label: 'Inventaire', icon: ClipboardList,  color: 'text-amber-600',   bg: 'bg-amber-50',     border: 'border-amber-200',     dot: 'bg-amber-500' },
    prospect:   { label: 'Prospect',   icon: User,           color: 'text-purple-600',  bg: 'bg-purple-50',    border: 'border-purple-200',    dot: 'bg-purple-500' },
};

function Indicator({ ok, label, sub }) {
    return (
        <div className={`flex items-start gap-2 rounded-lg p-3 border ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-white'}`}>
                {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700">{label}</p>
                {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

function ResultCard({ r, navigate }) {
    const meta = PHASE_META[r.primary_phase] || PHASE_META.prospect;
    const PhaseIcon = meta.icon;
    const open = () => {
        if (r.client_id) navigate(`/clients/${r.client_id}`);
        else if (r.inventaire_id) navigate(`/prospects/${r.prospect_id}/modifier?context=extended&from=inventaire`);
        else navigate(`/prospects/${r.prospect_id}`);
    };

    const s = r.summary || {};

    return (
        <div className={`bg-white rounded-xl border ${meta.border} overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
            {/* Header */}
            <div className={`px-5 py-4 ${meta.bg} flex items-start justify-between gap-3`}>
                <div className="flex items-start gap-3 min-w-0">
                    <div className={`shrink-0 w-11 h-11 rounded-full bg-white flex items-center justify-center border ${meta.border}`}>
                        <PhaseIcon className={`w-5 h-5 ${meta.color}`} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D] truncate">{r.nom}</h3>
                        <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                            {r.telephone && <span>{r.telephone}</span>}
                            {r.email && <span className="truncate">{r.email}</span>}
                        </div>
                    </div>
                </div>
                <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${meta.color} ${meta.bg} border ${meta.border}`}>
                    {meta.label}
                </span>
            </div>

            {/* Phases bar */}
            <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-2" data-testid={`search-phases-${r.prospect_id}`}>
                {['client', 'devis', 'inventaire', 'prospect'].map((p) => {
                    const has = r.phases.includes(p);
                    const m = PHASE_META[p];
                    const Icon = m.icon;
                    return (
                        <span
                            key={p}
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold border ${has ? `${m.color} ${m.bg} ${m.border}` : 'text-gray-300 bg-gray-50 border-gray-200'}`}
                        >
                            <Icon className="w-3 h-3" />
                            {m.label}
                            {p === 'devis' && has && r.devis_count > 0 && (
                                <span className="ml-0.5">×{r.devis_count}</span>
                            )}
                        </span>
                    );
                })}
            </div>

            {/* Summary contextualisé */}
            <div className="px-5 py-4 space-y-3">
                {r.primary_phase === 'client' && (
                    <>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            État du client
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <Indicator
                                ok={!!s.livraison_planifiee}
                                label="Livraison"
                                sub={s.livraison_planifiee ? 'Planifiée' : 'Non planifiée'}
                            />
                            <Indicator
                                ok={!!s.agenda_demenagement}
                                label="Agenda déménagement"
                                sub={s.agenda_demenagement ? 'Planifié' : 'Non planifié'}
                            />
                            <Indicator
                                ok={!!s.acompte_recu}
                                label="Acompte"
                                sub={s.acompte_recu ? `${(s.acompte_total || 0).toFixed(2)} €` : 'Non versé'}
                            />
                            <Indicator
                                ok={!!s.solde_recu}
                                label="Solde (terrain)"
                                sub={s.solde_recu ? `${(s.solde_total || 0).toFixed(2)} €` : 'Non encaissé'}
                            />
                            <Indicator
                                ok={!!(s.aot && s.aot.active)}
                                label="AOT"
                                sub={
                                    s.aot && s.aot.active
                                        ? `${s.aot.count} jour${s.aot.count > 1 ? 's' : ''} actif${s.aot.count > 1 ? 's' : ''}`
                                        : 'Aucun'
                                }
                            />
                            <Indicator
                                ok={!!(s.monte_meuble && s.monte_meuble.active)}
                                label="Monte-meuble"
                                sub={
                                    s.monte_meuble && s.monte_meuble.active
                                        ? `${s.monte_meuble.count} jour${s.monte_meuble.count > 1 ? 's' : ''} actif${s.monte_meuble.count > 1 ? 's' : ''}`
                                        : 'Aucun'
                                }
                            />
                            <Indicator
                                ok={!!(s.garde_meuble && s.garde_meuble.actif)}
                                label="Garde-meuble"
                                sub={s.garde_meuble?.actif
                                    ? `Actif${s.garde_meuble.count ? ` · ${s.garde_meuble.count} jour${s.garde_meuble.count > 1 ? 's' : ''}` : ''}${s.garde_meuble.duree ? ` · ${s.garde_meuble.duree}` : ''}`
                                    : 'Inactif'}
                            />
                        </div>
                    </>
                )}

                {r.primary_phase === 'devis' && (
                    <>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-blue-500" />
                            État du devis
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Statut actuel</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5 capitalize">{s.devis_statut || '—'}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Numéro</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5">{s.devis_numero || '—'}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 col-span-2 flex items-center justify-between">
                                <div>
                                    <p className="text-[11px] uppercase text-gray-400 tracking-wide">Relance</p>
                                    <p className="text-sm font-semibold text-[#1B365D] mt-0.5">
                                        {s.relance_dans_les_temps === undefined
                                            ? '—'
                                            : s.relance_dans_les_temps
                                                ? 'Dans les temps'
                                                : 'En retard'}
                                    </p>
                                </div>
                                <Clock className={`w-5 h-5 ${s.relance_dans_les_temps === false ? 'text-red-500' : 'text-emerald-500'}`} />
                            </div>
                            {s.devis_date_envoi && (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 col-span-2">
                                    <p className="text-[11px] uppercase text-gray-400 tracking-wide">Envoyé</p>
                                    <p className="text-sm text-gray-700 mt-0.5">
                                        {new Date(s.devis_date_envoi).toLocaleDateString('fr-FR')} {s.jours_depuis_envoi !== undefined && <span className="text-gray-400">· il y a {s.jours_depuis_envoi}j</span>}
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {r.primary_phase === 'inventaire' && (
                    <>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <ClipboardList className="w-3.5 h-3.5 text-amber-500" />
                            État de l'inventaire
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Statut</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5 capitalize">{s.inventaire_statut || 'En cours'}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Volume estimé</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5">{s.volume_estime ? `${s.volume_estime} m³` : '—'}</p>
                            </div>
                        </div>
                    </>
                )}

                {r.primary_phase === 'prospect' && (
                    <>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-purple-500" />
                            État du prospect
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Statut</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5 capitalize">{(s.statut_prospect || '—').replace(/_/g, ' ')}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-[11px] uppercase text-gray-400 tracking-wide">Source</p>
                                <p className="text-sm font-bold text-[#1B365D] mt-0.5">{s.source || '—'}</p>
                            </div>
                        </div>
                    </>
                )}

                <div className="pt-2 flex justify-end">
                    <button
                        onClick={open}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#2A4A7A] transition-colors"
                        data-testid={`search-open-${r.prospect_id}`}
                    >
                        Ouvrir la fiche →
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Recherche() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const inputRef = useRef(null);

    const runSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(q.trim())}`, { withCredentials: true });
            setResults(res.data || []);
        } catch {
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounce + keep URL in sync
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            runSearch(query);
            const urlParams = new URLSearchParams(searchParams);
            if (query.trim()) urlParams.set('q', query.trim());
            else urlParams.delete('q');
            setSearchParams(urlParams, { replace: true });
        }, 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    // Autofocus on mount
    useEffect(() => { inputRef.current?.focus(); }, []);

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D] flex items-center gap-2">
                        <SearchIcon className="w-6 h-6 text-[#C9A962]" />
                        Ma recherche
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Cherchez un nom, un numéro de devis, un téléphone ou un email pour retrouver instantanément où en est la personne dans tous les onglets.
                    </p>
                </div>

                {/* Search bar */}
                <div className="relative">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Nom, numéro de devis, téléphone, email…"
                        className="w-full pl-12 pr-12 py-4 bg-white border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:border-[#C9A962] transition-colors shadow-sm"
                        data-testid="search-input"
                    />
                    {loading && (
                        <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C9A962] animate-spin" />
                    )}
                </div>

                {/* Results */}
                <div className="space-y-4" data-testid="search-results">
                    {!query.trim() && (
                        <div className="text-center py-16 px-4">
                            <div className="inline-flex w-16 h-16 rounded-full bg-[#C9A962]/10 items-center justify-center mb-4">
                                <SearchIcon className="w-7 h-7 text-[#C9A962]" />
                            </div>
                            <p className="text-gray-500">
                                Tapez un nom, numéro de devis, téléphone ou email pour commencer.
                            </p>
                        </div>
                    )}
                    {query.trim() && !loading && results.length === 0 && (
                        <div className="text-center py-12 px-4 bg-gray-50 rounded-xl border border-gray-200">
                            <p className="text-gray-500 text-sm">Aucun résultat pour <strong className="text-[#1B365D]">{query}</strong>.</p>
                        </div>
                    )}
                    {results.length > 0 && (
                        <p className="text-xs text-gray-500">
                            {results.length} résultat{results.length > 1 ? 's' : ''} — triés par étape la plus avancée
                        </p>
                    )}
                    {results.map((r) => (
                        <ResultCard key={r.prospect_id} r={r} navigate={navigate} />
                    ))}
                </div>
            </div>
        </Layout>
    );
}
